import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  CartItemModel,
  CartItemModifierGroupModel,
  StoreProductModel,
} from "../models";
import { areModifiersEqual, cartActions } from "../utilities";
import { RootState } from ".";
import { ValidateCart } from "../services";
import { Toast } from "../components/Toast";

import { local } from "../embed/storage";
interface CartState {
  cartId: string;
  menuId: string;
  isLoading: boolean;
  items: CartItemModel[];
  cartStoreId: string | null;
  uusid: number;
}

const initialCartId = ((): string => {
  try {
    return local.getItem("cart_id") ?? "";
  } catch {
    return "";
  }
})();

const initialMenuId = ((): string => {
  try {
    return local.getItem("cart_menu_id") ?? "";
  } catch {
    return "";
  }
})();

const initialItems = ((): CartItemModel[] => {
  try {
    const raw = local.getItem("cart");
    return raw ? (JSON.parse(raw) as CartItemModel[]) : [];
  } catch {
    return [] as CartItemModel[];
  }
})();

const initialCartStoreId = ((): string | null => {
  try {
    const raw = local.getItem("cart_store_id");
    return raw ? (JSON.parse(raw) as string | null) : null;
  } catch {
    return null;
  }
})();

const initialUusid = ((): number => {
  try {
    const raw = local.getItem("uusid");
    return raw ? (JSON.parse(raw) as number) : 0;
  } catch {
    return 0;
  }
})();

const initialState: CartState = {
  cartId: initialCartId,
  menuId: initialMenuId,
  isLoading: false,
  items: initialItems,
  cartStoreId: initialCartStoreId,
  uusid: initialUusid,
};

// 🔥 Thunk: validate cart on server and update items
export const validateCart = createAsyncThunk<
  CartItemModel[],
  {}, // adjust `any` to your orderOption type
  { state: RootState; rejectValue: string }
>("cart/validateCart", async ({}, { getState, rejectWithValue }) => {
  const state = getState();
  const {
    cart: { items, cartId },
    store: { storeId, utm },
    auth: { customer, deviceId },
    orderOption: { value: orderOption },
  } = state;

  const cartQuantity = items.reduce(
    (q, item) => q + Number(item.product_qty || 0),
    0,
  );
  if (!cartQuantity) {
    return items;
  }

  try {
    const { status, message, data } = await ValidateCart({
      store_id: storeId,
      customer_id: customer?.id || "",
      device_id: deviceId,
      cart_id: cartId,
      cart: items,
      order_option: { ...orderOption, utm_source: utm },
    });

    if (!status) {
      Toast.error(message);
      return rejectWithValue(message);
    }

    // API returns the updated cart array (with price, discount, convenience, tax)
    return data as CartItemModel[];
  } catch (error) {
    console.error("Error validating cart:", error);
    return rejectWithValue("Error validating cart");
  }
});

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartId(state, action: PayloadAction<string>) {
      state.cartId = action.payload;
    },
    setMenuId(state, action: PayloadAction<string>) {
      state.menuId = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setCartStoreId(state, action: PayloadAction<string | null>) {
      state.cartStoreId = action.payload;
    },
    setUusid(state, action: PayloadAction<number>) {
      state.uusid = action.payload;
    },
    setCartItems(state, action: PayloadAction<CartItemModel[]>) {
      state.items = action.payload;
    },

    // Same logic as your addToCart useCallback
    addToCart(
      state,
      action: PayloadAction<{
        product: StoreProductModel;
        quantity: number;
        modifiers: CartItemModifierGroupModel[];
        instructions: string;
        menuId: string;
      }>,
    ) {
      const { product, quantity, modifiers, instructions, menuId } =
        action.payload;

      const cartMenuId = state.menuId;

      if (cartMenuId && cartMenuId !== menuId) {
        state.items = [];
      }

      state.menuId = menuId;

      const existingIndex = state.items.findIndex(
        (item) =>
          item.product_id === product.id &&
          areModifiersEqual(item.modifier_group, modifiers),
      );

      if (existingIndex === -1) {
        state.items.push({
          cart_item_id: "" + crypto.randomUUID(), // Date.now(),
          category_id: product.cid,
          product_id: product.id,
          product_name: product.product_name,
          product_price: product.product_price,
          product_qty: quantity,
          modifier_group: modifiers,
          product_discount: "0",
          product_disabled: "0",
          product_convenience: "0",
          product_tax: "0",
          product_instruction: instructions,
        });
      } else {
        state.items[existingIndex].product_qty += quantity;
      }
    },

    // Reuse your existing cartActions utilities
    increaseQuantity(state, action: PayloadAction<number | string>) {
      state.items = cartActions.increaseQuantity(state.items, action.payload);
    },
    decreaseQuantity(state, action: PayloadAction<number | string>) {
      state.items = cartActions.decreaseQuantity(state.items, action.payload);
    },

    updateCartItem(
      state,
      action: PayloadAction<{ index: number; cartItem: CartItemModel }>,
    ) {
      const { index, cartItem } = action.payload;
      state.items = state.items.map((item, i) =>
        i === index ? cartItem : item,
      );
    },

    removeFromCart(state, action: PayloadAction<number>) {
      const index = action.payload;
      state.items = state.items.filter((_, i) => i !== index);
    },

    clearCart(state) {
      state.cartId = "";
      state.menuId = "";
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload; // ✅ set returned cart in Redux
      })
      .addCase(validateCart.rejected, (state) => {
        state.isLoading = false;
        // we kept the old items; nothing changed
      });
  },
});

export const {
  setCartId,
  setMenuId,
  setIsLoading,
  setCartStoreId,
  setUusid,
  setCartItems,
  addToCart,
  increaseQuantity,
  decreaseQuantity,
  updateCartItem,
  removeFromCart,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;

export const selectCartItems = (state: RootState) => state.cart.items;

export const selectCartTotals = createSelector([selectCartItems], (items) => {
  let price = 0;
  let salePrice = 0;
  let discount = 0;
  let inflation = 0;
  let tax = 0;

  for (const item of items) {
    const qty = Number(item.product_qty ?? 0);
    price +=
      Number(item.product_price ?? 0) + Number(item.product_convenience ?? 0);
    salePrice +=
      Number(item.product_price ?? 0) +
      Number(item.product_convenience ?? 0) -
      Number(item.product_discount);
    discount += Number(item.product_discount ?? 0);
    // these fields exist on your validateCart response
    inflation += Number(item.product_convenience ?? 0) * qty;
    tax +=
      Number(item.product_tax ?? 0) + Number(item.product_convenience_tax ?? 0);
  }

  // IMPORTANT: createSelector memoizes this object
  return {
    price,
    discount,
    inflation,
    tax,
    salePrice,
  };
});
